import { useState } from 'react'
import {
  Space,
  Typography,
  Tooltip,
  Row,
  Col,
  Form,
  Modal as ModalAntd,
  Radio as RadioAntd,
  notification,
} from 'antd'
import { SettingOutlined, DeleteOutlined, LeftOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import _ from 'lodash'
import { useHistory, useParams } from 'react-router-dom'
import { useAsync } from 'react-use'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Table from 'components/Table'
import Modal from 'components/Modal'
import Input from 'components/Input'
import Select from 'components/Select'
import AllActiveInactive from 'components/AllActiveInactive'

import { useTranslate } from 'hooks/translate'

import { getDictionaryByIdApi } from 'services/dictionary'
import {
  getDictionaryValueListApi,
  createDictionaryValueApi,
  deleteDictionaryValueByIdApi,
  updateDictionaryValueByIdApi,
} from 'services/dictionary/value'

import Styled from './styled'

const DEFAULT_PAGE_SIZE = 10

export default function DictionaryDetail () {
  const { t } = useTranslate()
  const history = useHistory()
  const { id } = useParams()

  const [form] = Form.useForm()

  const [dictionary, setDictionary] = useState({})
  const [dataSource, setDataSource] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    param: null,
    page: 1,
    size: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all') // all, active, inactive
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const getDictionaryById = async (id) => {
    const res = await getDictionaryByIdApi(id)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setDictionary(res?.data)
  }

  const _list = async () => {
    if (loading) return

    const params = {
      dictionary_id: id,
      ...filters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setLoading(true)

    const res = await getDictionaryValueListApi(params)

    setLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setTotal(res.data?.total)
    setDataSource(res.data?.items)
  }

  const _create = async (values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await createDictionaryValueApi({
      dictionary_id: dictionary?.id,
      dictionary_value_code: values?.key,
      dictionary_value_name: values?.value,
      is_significant: values?.isSignificant,
      is_active: values?.isEnabled,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form.resetFields()
  }

  const _update = async (id, values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await updateDictionaryValueByIdApi({
      id,
      dictionary_id: dictionary?.id,
      dictionary_value_code: values?.key,
      dictionary_value_name: values?.value,
      is_significant: values?.isSignificant,
      is_active: values?.isEnabled,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form.resetFields()
  }

  const _delete = async (id) => {
    if (deleting) return

    setDeleting(true)

    const res = await deleteDictionaryValueByIdApi(id)

    setDeleting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()
  }

  useAsync(async () => {
    _list()
  }, [status, filters])

  useAsync(async () => {
    if (!id) {
      history.push('./')

      return
    }

    await getDictionaryById(id)
  }, [id])

  const columns = [
    {
      title: t('Key'),
      dataIndex: 'code',
      key: 'code',
      className: 'bold',
    },
    {
      title: t('Value'),
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: t('Status'),
      dataIndex: ['status', 'label'],
      key: 'status',
      className: 'is-status',
      render: (text, record, index) => {
        return (
          <div
            className={classNames('status-block', {
              'is-active': record.isEnabled,
              'is-inactive': !record.isEnabled,
            })}
          >
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      render: (text, record, index) => {
        return (
          <Space size='small'>
            <Tooltip title={t('Edit')}>
              <Button
                type='link'
                icon={<SettingOutlined />}
                onClick={() => {
                  setEditingId(record?.id)

                  const editingDataSource = _.find(dataSource, { id: record?.id })

                  form.setFieldsValue({
                    key: editingDataSource.code,
                    value: editingDataSource.value,
                    isSignificant: editingDataSource.isSignificant,
                    isEnabled: editingDataSource.isEnabled,
                  })

                  setShowCreateModal(true)
                }}
              />
            </Tooltip>
            <Tooltip title={t('Delete')}>
              <Button
                type='link'
                icon={<DeleteOutlined />}
                onClick={() => {
                  const deletingDataSource = _.find(dataSource, { id: record?.id })

                  ModalAntd.confirm({
                    title: 'Confirm deletion: Dictionary',
                    icon: null,
                    maskClosable: true,
                    closeIcon: true,
                    content: (
                      <div>
                        <dl>
                          <dt>Key</dt>
                          <dd>- {deletingDataSource.code}</dd>
                          <dt>Value</dt>
                          <dd>- {deletingDataSource.value}</dd>
                        </dl>
                      </div>
                    ),
                    okButtonProps: {
                      shape: 'round',
                    },
                    okText: 'Delete',
                    okType: 'primary',
                    cancelButtonProps: {
                      shape: 'round',
                    },
                    cancelText: 'Cancel',
                    onOk: async () => {
                      await _delete(record?.id)
                    },
                  })
                }}
              />
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  return (
    <Styled className='page page-dictionary-detail'>
      <Page
        title={t('Dictionary')}
        searchPlaceholder={t('Search Dictionary or description')}
        onSearch={(value) => {
          setFilters({
            ...filters,
            param: value,
            page: 1,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Row justify='space-between' align='middle'>
            <Col>
              <Button icon={<LeftOutlined />} onClick={() => history.push('./')}>
                {t('Back')}
              </Button>
            </Col>
            <Col>
              <Button type='primary' size='large' onClick={() => setShowCreateModal(true)}>
                {t('Create New Key and Value')}
              </Button>
            </Col>
          </Row>
          <Card
            title={dictionary?.name}
            extra={
              <>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </>
            }
          >
            <Table
              loading={loading}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                current: filters.page,
                pageSize: filters.size,
                total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total) => t('Total ${total} items', { total }),
              }}
              onChange={(pagination, filters) => {
                setFilters({
                  ...filters,
                  page: pagination.current,
                  size: pagination.pageSize,
                })
              }}
              scroll={{ x: 800 }}
              sticky
            />
          </Card>
        </Space>
      </Page>

      <Modal
        width={600}
        visible={showCreateModal}
        title={editingId ? t('Update Key-Value') : t('Create New Key-Value')}
        onCancel={() => {
          setShowCreateModal(false)
        }}
        afterClose={() => {
          if (editingId) {
            form.resetFields()
          }

          setEditingId(null)
        }}
        footer={[
          <Button
            key='create'
            type='primary'
            onClick={() => {
              form.submit()
            }}
          >
            {editingId ? t('Update') : t('Create')}
          </Button>,
        ]}
      >
        <div>
          <Form
            form={form}
            layout='vertical'
            initialValues={{
              isSignificant: true,
              isEnabled: true,
            }}
            onFinish={(values) => {
              if (editingId) {
                _update(editingId, values)
              } else {
                _create(values)
              }
            }}
          >
            <Form.Item label={t('Key')} name='key' rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item label={t('Value')} name='value' rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Row justify='space-between'>
              <Col>
                <Form.Item name='isSignificant' rules={[{ required: true }]}>
                  <RadioAntd.Group>
                    <RadioAntd value={true}>{t('Significant')}</RadioAntd>
                    <RadioAntd value={false}>{t('Non-significant')}</RadioAntd>
                  </RadioAntd.Group>
                </Form.Item>
              </Col>

              <Col>
                <Form.Item name='isEnabled'>
                  <Select
                    options={[
                      { label: t('Active'), value: true },
                      { label: t('Inactive'), value: false },
                    ]}
                    style={{ width: 200, textAlign: 'center' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </Styled>
  )
}
